import type { TextBox, TextChain } from '@/api/types/stowed-element'

/** True when `uid` has an outgoing link (is not the last member of its chain). */
export function hasOutgoingLink(chains: TextChain[], uid: string): boolean {
  const chain = chainContaining(chains, uid)

  if (!chain) return false
  const idx = chain.textBox.findIndex((tb) => tb.uid === uid)

  return idx >= 0 && idx < chain.textBox.length - 1
}

/** True when `uid` has an incoming link (is not the first member of its chain). */
export function hasIncomingLink(chains: TextChain[], uid: string): boolean {
  const chain = chainContaining(chains, uid)

  if (!chain) return false
  const idx = chain.textBox.findIndex((tb) => tb.uid === uid)

  return idx > 0
}

export function chainContaining(chains: TextChain[], uid: string): TextChain | null {
  return chains.find((chain) => chain.textBox.some((tb) => tb.uid === uid)) ?? null
}

/**
 * Returns every distinct chain containing at least one of `uids`, de-duplicated by chain id.
 * Used when a batch operation (e.g. multi-select resize) touches several chain members at once —
 * callers should reflow each affected chain exactly once, not once per touched box (WMPCM-6852).
 */
export function chainsContainingAny(chains: TextChain[], uids: string[]): TextChain[] {
  const seen = new Set<string>()
  const result: TextChain[] = []

  for (const uid of uids) {
    const chain = chainContaining(chains, uid)

    if (chain && !seen.has(chain.id)) {
      seen.add(chain.id)
      result.push(chain)
    }
  }

  return result
}

export function nextUidInChain(chains: TextChain[], uid: string): string | null {
  const chain = chainContaining(chains, uid)

  if (!chain) return null
  const idx = chain.textBox.findIndex((tb) => tb.uid === uid)

  if (idx < 0 || idx >= chain.textBox.length - 1) return null
  return chain.textBox[idx + 1]!.uid
}

export function prevUidInChain(chains: TextChain[], uid: string): string | null {
  const chain = chainContaining(chains, uid)

  if (!chain) return null
  const idx = chain.textBox.findIndex((tb) => tb.uid === uid)

  if (idx <= 0) return null
  return chain.textBox[idx - 1]!.uid
}

/**
 * Returns the members of `chain` from `uid` onward (inclusive) — `uid` and everything downstream
 * of it, never anything upstream. An upstream frame's fit-state can never be affected by a
 * downstream change, so it must never be included in the frames handed to `distributeStory`.
 * Returns `[]` when `uid` isn't a member of `chain`.
 */
export function chainMembersFrom(chain: TextChain, uid: string): TextBox[] {
  const idx = chain.textBox.findIndex((tb) => tb.uid === uid)

  if (idx < 0) return []
  return chain.textBox.slice(idx)
}
